import React from 'react';
import * as d3 from 'd3';
import { useResponsiveD3, useD3Tooltip } from '../../../hooks/useD3';

const GeographicChart = ({ data }) => {
  const { showTooltip, hideTooltip } = useD3Tooltip();

  const renderChart = (svg, { width, height }) => {
    const margin = { top: 20, right: 20, bottom: 40, left: 40 };
    const chartWidth = width - margin.left - margin.right;
    const chartHeight = height - margin.top - margin.bottom;

    // Create main group
    const g = svg.append('g')
      .attr('transform', `translate(${margin.left},${margin.top})`);

    // Sample geographic data - districts and their centers/ports
    const geographicData = [
      { district: 'District A', centers: 5, ports: 2, customers: 150 },
      { district: 'District B', centers: 3, ports: 1, customers: 89 },
      { district: 'District C', centers: 7, ports: 3, customers: 234 },
      { district: 'District D', centers: 4, ports: 2, customers: 167 },
      { district: 'District E', centers: 6, ports: 1, customers: 198 },
      { district: 'District F', centers: 2, ports: 1, customers: 78 },
      { district: 'District G', centers: 8, ports: 4, customers: 289 },
      { district: 'District H', centers: 3, ports: 2, customers: 123 }
    ];

    // Use actual data if available
    const actualData = data?.districts || geographicData;

    // Scales
    const xScale = d3.scaleBand()
      .domain(actualData.map(d => d.district || d.name))
      .range([0, chartWidth])
      .padding(0.1);

    const yScale = d3.scaleLinear()
      .domain([0, d3.max(actualData, d => Math.max(d.centers || d.centerCount || 0, d.ports || 0))])
      .range([chartHeight, 0]);

    // Color scale with better contrast
    const colorScale = d3.scaleOrdinal()
      .domain(['centers', 'ports'])
      .range(['#2563EB', '#059669']);

    // Stack data for grouped bar chart
    const keys = ['centers', 'ports'];
    const stackedData = actualData.map(d => ({
      district: d.district || d.name,
      centers: d.centers || d.centerCount || 0,
      ports: d.ports || 0,
      customers: d.customers || 0
    }));

    // Create groups for each district
    const districtGroups = g.selectAll('.district-group')
      .data(stackedData)
      .enter().append('g')
      .attr('class', 'district-group')
      .attr('transform', d => `translate(${xScale(d.district)}, 0)`);

    const barWidth = xScale.bandwidth() / 2;

    // Centers bars
    districtGroups.append('rect')
      .attr('class', 'centers-bar')
      .attr('x', 0)
      .attr('width', barWidth - 2)
      .attr('y', chartHeight)
      .attr('height', 0)
      .attr('fill', colorScale('centers'))
      .attr('rx', 2)
      .on('mouseover', function(event, d) {
        d3.select(this).attr('opacity', 0.8);
        showTooltip(`${d.district}<br>Centers: ${d.centers}<br>Customers: ${d.customers || 0}`, event);
      })
      .on('mouseout', function() {
        d3.select(this).attr('opacity', 1);
        hideTooltip();
      })
      .transition()
      .duration(800)
      .attr('y', d => yScale(d.centers))
      .attr('height', d => chartHeight - yScale(d.centers));

    // Ports bars
    districtGroups.append('rect')
      .attr('class', 'ports-bar')
      .attr('x', barWidth)
      .attr('width', barWidth - 2)
      .attr('y', chartHeight)
      .attr('height', 0)
      .attr('fill', colorScale('ports'))
      .attr('rx', 2)
      .on('mouseover', function(event, d) {
        d3.select(this).attr('opacity', 0.8);
        showTooltip(`${d.district}<br>Ports: ${d.ports}`, event);
      })
      .on('mouseout', function() {
        d3.select(this).attr('opacity', 1);
        hideTooltip();
      })
      .transition()
      .duration(800)
      .delay(200)
      .attr('y', d => yScale(d.ports))
      .attr('height', d => chartHeight - yScale(d.ports));

    // X-axis with better text formatting
    g.append('g')
      .attr('transform', `translate(0,${chartHeight})`)
      .call(d3.axisBottom(xScale))
      .selectAll('text')
      .style('font-size', '11px')
      .style('font-weight', '500')
      .style('fill', '#374151')
      .attr('transform', 'rotate(-35)')
      .style('text-anchor', 'end')
      .attr('dx', '-0.5em')
      .attr('dy', '0.15em');

    // Y-axis with better styling
    g.append('g')
      .call(d3.axisLeft(yScale))
      .style('font-size', '12px')
      .style('color', '#6B7280');

    // Y-axis label
    g.append('text')
      .attr('transform', 'rotate(-90)')
      .attr('y', 0 - margin.left)
      .attr('x', 0 - (chartHeight / 2))
      .attr('dy', '1em')
      .style('text-anchor', 'middle')
      .style('font-size', '12px')
      .style('fill', '#6B7280')
      .text('Count');

    // Legend with better positioning
    const legend = g.append('g')
      .attr('class', 'legend')
      .attr('transform', `translate(${chartWidth - 100}, 20)`);

    const legendData = [
      { label: 'Centers', color: colorScale('centers') },
      { label: 'Ports', color: colorScale('ports') }
    ];

    const legendItems = legend.selectAll('.legend-item')
      .data(legendData)
      .enter().append('g')
      .attr('class', 'legend-item')
      .attr('transform', (d, i) => `translate(0, ${i * 22})`);

    legendItems.append('rect')
      .attr('width', 14)
      .attr('height', 14)
      .attr('fill', d => d.color)
      .attr('rx', 3)
      .attr('stroke', '#fff')
      .attr('stroke-width', 1);

    legendItems.append('text')
      .attr('x', 18)
      .attr('y', 7)
      .attr('dy', '0.35em')
      .style('font-size', '13px')
      .style('font-weight', '500')
      .style('fill', '#374151')
      .text(d => d.label);
  };

  const { containerRef, svgRef } = useResponsiveD3(
    renderChart, 
    [data], 
    2.5
  );

  return (
    <div ref={containerRef} className="w-full h-64">
      <svg ref={svgRef} className="w-full h-full"></svg>
    </div>
  );
};

export default GeographicChart;